import React from 'react'
import { List, useTable, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space, Alert, Typography } from 'antd'
import { Link } from 'react-router'
import { parsePostMedia, type Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, TableSurface, Pill } from '../../theme/listkit'

type UserPost = Tables<'user_posts'>
type Profile = Tables<'profiles'>

/**
 * Member Posts — the Supabase ARCHIVE of member-authored feed posts.
 *
 * READ-ONLY BY DESIGN. This page used to carry a per-row DeleteButton under copy
 * promising it would "remove any post that violates the community guidelines".
 * It did no such thing: `user_posts` stores no Stream activity id (columns are
 * id, author_id, body, image_urls, created_at, media), so deleting the row
 * destroyed the durable archive record while leaving the offending post fully
 * live in every member's feed. Staff would reasonably believe the takedown had
 * worked. The same trap is documented at feed-moderate/index.ts and
 * useCreatePost.ts.
 *
 * Removal belongs to Feed Moderation, which drives the `feed-moderate` Edge
 * Function against Stream itself: the post actually disappears, it is reversible
 * by default, and hard delete is gated behind typing the activity id.
 *
 * The author column exists because the old page had none — staff could not see
 * whose post they were about to destroy.
 */
export function UserPostList() {
  const { tableProps } = useTable<UserPost>({
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })

  // Resolve author_id -> profiles.name, scoped to ONLY the authors on the
  // current page (IN filter), not the whole profiles table.
  const rows = (tableProps.dataSource ?? []) as UserPost[]
  const authorIds = React.useMemo(
    () => Array.from(new Set(rows.map((p) => p.author_id).filter(Boolean))) as string[],
    [rows],
  )
  const { result: profileRes } = useList<Profile>({
    resource: 'profiles',
    filters: authorIds.length ? [{ field: 'user_id', operator: 'in', value: authorIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: authorIds.length > 0 },
  })
  const authorMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileRes?.data ?? []) as Profile[]
    return Object.fromEntries(
      items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]),
    )
  }, [profileRes])

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Feed Moderation',
        description:
          'The stored archive of member-authored Activity Feed posts ("Share an update"). This is a read-only record of what was written — it is not the live feed.',
      }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="To take a post down, use Feed Moderation"
        description={
          <>
            Removing a row here would <Typography.Text strong>not</Typography.Text> remove the post
            from the feed — this table holds no Stream activity id, so the post would stay visible
            to every member while the archive record disappeared. Deletion is therefore not offered
            on this page. Use{' '}
            <Link to="/feed-moderation">Trust & Safety › Feed Moderation</Link>, which removes the activity
            from Stream itself, is reversible by default, and records the action in the audit log.
          </>
        }
      />

      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }}>
            <Table.Column<UserPost>
              dataIndex="body"
              title="Post"
              render={(body: string) => (
                <span style={{ fontFamily: brand.fSans, color: brand.ink }}>{body}</span>
              )}
            />
            <Table.Column<UserPost>
              dataIndex="author_id"
              title="Author"
              render={(id: string | null) => (
                <span style={{ fontFamily: brand.fSans, color: id ? brand.ink : brand.sub }}>
                  {(id && authorMap[id]) || '—'}
                </span>
              )}
            />
            <Table.Column<UserPost>
              dataIndex="media"
              title="Media"
              render={(_: unknown, record: UserPost) => {
                const media = parsePostMedia(record.media)
                const images = media.filter((m) => m.type === 'image').length
                const videos = media.filter((m) => m.type === 'video').length
                if (images === 0 && videos === 0) return <span style={{ color: brand.sub }}>—</span>
                return (
                  <Space size={4}>
                    {images > 0 ? (
                      <Pill tone="gold">{`${images} photo${images === 1 ? '' : 's'}`}</Pill>
                    ) : null}
                    {videos > 0 ? <Pill tone="jaguar">Video</Pill> : null}
                  </Space>
                )
              }}
            />
            <Table.Column<UserPost>
              dataIndex="created_at"
              title="Posted"
              render={(value: string) => <DateField value={value} format="MMM D, YYYY h:mm A" />}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
